"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  Flame,
  Star,
  Sparkles,
  Images,
} from "lucide-react";
import { useLanguage } from "@/app/context/LanguageContext";

// ── Image catalogue ──────────────────────────────────────────────────────────
const IMAGES = [
  { id: 1,  src: "/image-gallery/img1.jpg",   altEn: "Majestic Entrance of Sri 18m Padi Karuppanasamy Temple",   altTa: "ஸ்ரீ 18ம் படி கருப்பண்ணசாமி ஆலயத்தின் கம்பீரமான ராஜகோபுரத் தரிசனம்",  category: "temple",   labelEn: "Sacred Space",    labelTa: "புனித தலம்" },
  { id: 2,  src: "/image-gallery/img2.jpg",   altEn: "Sacred Evening Aarti - Divine Light Ceremony",             altTa: "புனித மாலை நேர தீப ஆராதனை - தெய்வீக ஒளி வழிபாடு",                      category: "rituals",  labelEn: "Divine Rituals",  labelTa: "தெய்வீக சடங்குகள்" },
  { id: 3,  src: "/image-gallery/img3.jpg",   altEn: "Grand Festival Procession - Swami Veedhi Ula",             altTa: "சுவாமி வீதி உலா - பிரம்மாண்ட திருவிழா ஊர்வலம்",                        category: "festival", labelEn: "Sacred Festivals",labelTa: "புனித திருவிழாக்கள்" },
  { id: 4,  src: "/image-gallery/img4.jpg",   altEn: "Karuppanasamy Divine Sanctum - Sacred Darshan",            altTa: "கருப்பண்ணசாமி கருவறை தரிசனம் - தெய்வீக அருள் காட்சி",                  category: "deity",    labelEn: "Divine Darshan",  labelTa: "தெய்வீக தரிசனம்" },
  { id: 5,  src: "/image-gallery/img5.jpg",   altEn: "Temple Architecture - Sacred Gopuram Splendour",           altTa: "ஆலய கட்டிடக்கலை - ராஜகோபுரத்தின் புனிதத் தோற்றம்",                    category: "temple",   labelEn: "Sacred Space",    labelTa: "புனித தலம்" },
  { id: 6,  src: "/image-gallery/img6.jpg",   altEn: "Ritual Offerings - Sacred Flowers for the Deity",          altTa: "வழிபாட்டு மலர்கள் - சுவாமிக்கு சமர்ப்பிக்கப்படும் புனித மலர்கள்",    category: "rituals",  labelEn: "Divine Rituals",  labelTa: "தெய்வீக சடங்குகள்" },
  { id: 7,  src: "/image-gallery/img7.jpg",   altEn: "Traditional Chariot Festival - Devotional Journey",        altTa: "பாரம்பரிய தேரோட்டம் - பக்திப்பூர்வமான திருவிழா பயணம்",                category: "festival", labelEn: "Sacred Festivals",labelTa: "புனித திருவிழாக்கள்" },
  { id: 8,  src: "/image-gallery/img8.jpg",   altEn: "Inner Sanctum - The Abode of Lord Karuppanasamy",          altTa: "கருவறை - கருப்பண்ணசாமி வீற்றிருக்கும் புனித இடம்",                     category: "deity",    labelEn: "Divine Darshan",  labelTa: "தெய்வீக தரிசனம்" },
  { id: 9,  src: "/image-gallery/img9.jpg",   altEn: "Sacred Temple Pond - Serenity at Dawn",                    altTa: "ஆலய புனித புஷ்கரணி - விடியலில் அமைதியான குளம்",                       category: "temple",   labelEn: "Sacred Space",    labelTa: "புனித தலம்" },
  { id: 10, src: "/image-gallery/img10.jpg",  altEn: "Divine Abhishekam - Sacred Cleansing Ritual",              altTa: "தெய்வீக அபிஷேகம் - புனித தீர்த்த சடங்கு",                             category: "rituals",  labelEn: "Divine Rituals",  labelTa: "தெய்வீக சடங்குகள்" },
  { id: 11, src: "/image-gallery/img11.jpg",  altEn: "Kavadi Procession - Devotees' Sacred Vow",                 altTa: "காவடி ஊர்வலம் - பக்தர்களின் நேர்த்திக்கடன்",                          category: "festival", labelEn: "Sacred Festivals",labelTa: "புனித திருவிழாக்கள்" },
  { id: 12, src: "/image-gallery/img12.jpg",  altEn: "Alankaram - Deity Adorned with Divine Jewels",             altTa: "அலங்காரத் தரிசனம் - ஆபரணங்கள் பூண்ட தெய்வீக விக்ரகம்",               category: "deity",    labelEn: "Divine Darshan",  labelTa: "தெய்வீக தரிசனம்" },
  { id: 13, src: "/image-gallery/img13.jpg",  altEn: "Sacred Deepam - Eternal Flame of the Sanctum",             altTa: "கருவறை தீபம் - அணையாத புனித விளக்கு",                                 category: "rituals",  labelEn: "Divine Rituals",  labelTa: "தெய்வீக சடங்குகள்" },
  { id: 14, src: "/image-gallery/img14.jpg",  altEn: "Main Entrance Gate - Gateway to Divinity",                 altTa: "ஆலய பிரதான நுழைவு வாயில் - ஆன்மீகத்தின் வாசல்",                      category: "temple",   labelEn: "Sacred Space",    labelTa: "புனித தலம்" },
  { id: 15, src: "/image-gallery/img15.jpg",  altEn: "Devotional Nadaswaram - Sacred Temple Music",              altTa: "பக்தி இசை - மேளம் மற்றும் நாதஸ்வர இசை வழிபாடு",                      category: "rituals",  labelEn: "Divine Rituals",  labelTa: "தெய்வீக சடங்குகள்" },
  { id: 16, src: "/image-gallery/img16.jpg",  altEn: "Utsava Vigraha - Procession Deity Darshan",                altTa: "உற்சவ மூர்த்தி தரிசனம் - ஊர்வல சுவாமி காட்சி",                        category: "festival", labelEn: "Sacred Festivals",labelTa: "புனித திருவிழாக்கள்" },
  { id: 17, src: "/image-gallery/img17.jpg",  altEn: "Sacred Temple Chariot - Divine Vehicle",                   altTa: "புனித திருக்கோயில் தேர் - தெய்வீக வாகனம்",                             category: "festival", labelEn: "Sacred Festivals",labelTa: "புனித திருவிழாக்கள்" },
  { id: 18, src: "/image-gallery/img18.jpg",  altEn: "Sacred Bells - Resonance of Devotion",                     altTa: "ஆலய மணிகள் - பக்தி அதிர்வுகளின் ஒலி",                                 category: "temple",   labelEn: "Sacred Space",    labelTa: "புனித தலம்" },
  { id: 19, src: "/image-gallery/img19.jpg",  altEn: "Sandal Paste Abhishekam - Fragrant Ritual",                altTa: "சந்தன அபிஷேகம் - நறுமணமிக்க புனித சடங்கு",                             category: "deity",    labelEn: "Divine Darshan",  labelTa: "தெய்வீக தரிசனம்" },
  { id: 20, src: "/image-gallery/img20.jpg",  altEn: "Illuminated Gopuram - Temple Splendour at Night",          altTa: "ஒளிர்ந்த கோபுரம் - இரவில் ஆலயத்தின் ராஜ தோற்றம்",                    category: "temple",   labelEn: "Sacred Space",    labelTa: "புனித தலம்" },
  { id: 21, src: "/image-gallery/img21.jpg",  altEn: "Homa Kundam - Sacred Fire Sacrifice",                      altTa: "ஹோம குண்டம் - புனித வேள்வி மற்றும் யாகம்",                             category: "rituals",  labelEn: "Divine Rituals",  labelTa: "தெய்வீக சடங்குகள்" },
  { id: 22, src: "/image-gallery/img22.jpg",  altEn: "Gathering of Devotees - Festival Celebration",             altTa: "பக்தர்கள் கூடுகை - திருவிழா கொண்டாட்டத்தின் காட்சி",                  category: "festival", labelEn: "Sacred Festivals",labelTa: "புனித திருவிழாக்கள்" },
  { id: 23, src: "/image-gallery/img23.jpg",  altEn: "Pushpa Alankaram - Divine Floral Decoration",              altTa: "புஷ்ப அலங்காரம் - மலர்களால் ஆன தெய்வீக அலங்காரம்",                    category: "deity",    labelEn: "Divine Darshan",  labelTa: "தெய்வீக தரிசனம்" },
  { id: 24, src: "/image-gallery/img24.jpg",  altEn: "Sacred Temple Arch - Traditional Welcoming",               altTa: "புனித ஆலய வளைவு - பாரம்பரிய வரவேற்பு நுழைவு",                         category: "temple",   labelEn: "Sacred Space",    labelTa: "புனித தலம்" },
  { id: 25, src: "/image-gallery/img25.jpg",  altEn: "Evening Deepam Display - Wall of Lights",                  altTa: "மாலை நேர விளக்கு அலங்காரம் - அகல் விளக்கு தரிசனம்",                   category: "rituals",  labelEn: "Divine Rituals",  labelTa: "தெய்வீக சடங்குகள்" },
  { id: 26, src: "/image-gallery/img26.jpg",  altEn: "Festival Illumination - Vibrant Temple Lights",            altTa: "திருவிழா மின்விளக்கு அலங்காரம் - வண்ணமயமான ஆலயக் காட்சி",             category: "festival", labelEn: "Sacred Festivals",labelTa: "புனித திருவிழாக்கள்" },
  { id: 27, src: "/image-gallery/img27.jpeg", altEn: "Prakaram - Sacred Circumambulation Path",                  altTa: "ஆலய பிரகாரம் - புனித வலம் வரும் பாதை",                                category: "temple",   labelEn: "Sacred Space",    labelTa: "புனித தலம்" },
  { id: 28, src: "/image-gallery/img28.jpeg", altEn: "Veda Parayanam - Sacred Chanting Ceremony",                altTa: "வேத பாராயணம் - புனித மந்திரங்கள் முழங்கும் நிகழ்வு",                  category: "rituals",  labelEn: "Divine Rituals",  labelTa: "தெய்வீக சடங்குகள்" },
  { id: 29, src: "/image-gallery/img29.jpeg", altEn: "Community Feast - Annadhanam Service",                     altTa: "அன்னதான சேவை - பக்திப்பூர்வமான உணவுப் பரிமாறல்",                     category: "festival", labelEn: "Sacred Festivals",labelTa: "புனித திருவிழாக்கள்" },
  { id: 30, src: "/image-gallery/img30.jpeg", altEn: "Karuppanasamy - Detailed Divine Darshan",                  altTa: "கருப்பண்ணசாமியின் அழகிய திருமேனி தரிசனம்",                            category: "deity",    labelEn: "Divine Darshan",  labelTa: "தெய்வீக தரிசனம்" },
  { id: 31, src: "/image-gallery/img31.jpeg", altEn: "Sacred Temple Archway - Detailed Carvings",                altTa: "ஆலய வளைவு - கலைநயமிக்க சிற்பக் காட்சிகள்",                           category: "temple",   labelEn: "Sacred Space",    labelTa: "புனித தலம்" },
  { id: 32, src: "/image-gallery/img32.jpeg", altEn: "Morning Rituals - Daily Divine Offerings",                 altTa: "காலை வழிபாடுகள் - தினசரி நைவேத்திய சடங்குகள்",                       category: "rituals",  labelEn: "Divine Rituals",  labelTa: "தெய்வீக சடங்குகள்" },
  { id: 33, src: "/image-gallery/img33.jpeg", altEn: "Grand Chariot Decoration - Festival Art",                  altTa: "அலங்கரிக்கப்பட்ட தேர் - திருவிழா கலைநயம்",                            category: "festival", labelEn: "Sacred Festivals",labelTa: "புனித திருவிழாக்கள்" },
  { id: 34, src: "/image-gallery/img34.jpeg", altEn: "Special Alankaram - Majestic Divine Pose",                 altTa: "சிறப்பு அலங்காரம் - கம்பீரமான தெய்வீக திருக்கோலம்",                  category: "deity",    labelEn: "Divine Darshan",  labelTa: "தெய்வீக தரிசனம்" },
  { id: 35, src: "/image-gallery/img35.jpeg", altEn: "Sunset over the Temple - Spiritual Calm",                  altTa: "சூரிய அஸ்தமனத்தில் ஆலயம் - ஆன்மீக அமைதி",                             category: "temple",   labelEn: "Sacred Space",    labelTa: "புனித தலம்" },
  { id: 36, src: "/image-gallery/img36.jpeg", altEn: "Agni Valarthal - Sacred Fire Ritual",                      altTa: "அக்னி வளர்த்தல் - புனித தீ வளர்க்கும் சடங்கு",                        category: "rituals",  labelEn: "Divine Rituals",  labelTa: "தெய்வீக சடங்குகள்" },
] as const;

type Category = "all" | "temple" | "rituals" | "festival" | "deity";

// Category accent colour helper -  Tailwind only (no dynamic strings)
const catBadgeClass: Record<string, string> = {
  temple:   "border-sky-700/60   bg-sky-950/50   text-sky-300",
  rituals:  "border-orange-700/60 bg-orange-950/50 text-orange-300",
  festival: "border-violet-700/60 bg-violet-950/50 text-violet-300",
  deity:    "border-amber-700/60  bg-amber-950/50  text-amber-300",
};

export default function ImageGalleryPage() {
  const { language, t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState<Category>("all");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  const filtered = IMAGES.filter(
    (img) => activeCategory === "all" || img.category === activeCategory
  );

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prev = () =>
    setLightboxIndex((i) => (i === null ? null : (i - 1 + filtered.length) % filtered.length));
  const next = () =>
    setLightboxIndex((i) => (i === null ? null : (i + 1) % filtered.length));

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxIndex]);

  const currentImage = lightboxIndex !== null ? filtered[lightboxIndex] : null;

  const CATEGORIES: { value: Category; label: string }[] = [
    { value: "all",     label: t.gallery.categories.all },
    { value: "temple",  label: t.gallery.categories.temple },
    { value: "rituals", label: t.gallery.categories.rituals },
    { value: "festival",label: t.gallery.categories.festival },
    { value: "deity",   label: t.gallery.categories.deity },
  ];

  return (
    <main className="min-h-screen bg-stone-950 text-stone-100 font-serif overflow-x-hidden selection:bg-amber-900 selection:text-amber-100">

      {/* ── HERO ──────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">

        {/* atmospheric layers */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-950/35 via-stone-950/70 to-stone-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_55%_at_50%_0%,rgba(180,83,9,0.2),transparent)]" />

        {/* concentric decorative rings */}
        <div className="absolute top-full left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-[50%] border border-amber-700/8" />
        <div className="absolute top-full left-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[280px] rounded-[50%] border border-amber-600/10" />

        {/* top ornament line */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-600/70 to-transparent" />

        <div className="relative z-10 mx-auto max-w-5xl px-6 py-28 text-center space-y-6">

          {/* Om line */}
          <p className="font-sans text-[10px] tracking-[0.45em] uppercase text-amber-500/70">
            ॐ &nbsp;
            {language === "ta" ? "ஸ்ரீ கருப்பண்ணசாமி" : "SRI KARUPPANNASWAMY"}
            &nbsp; ॐ
          </p>

          {/* title */}
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold leading-tight tracking-tight bg-gradient-to-br from-amber-100 via-yellow-400 to-orange-500 bg-clip-text text-transparent drop-shadow-lg">
            {t.gallery.imageTitle}
          </h1>

          {/* ornament divider */}
          <div className="flex items-center justify-center gap-3 py-1">
            <div className="h-px w-20 bg-gradient-to-r from-transparent to-amber-600" />
            <Flame className="h-4 w-4 text-amber-500" />
            <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
            <Flame className="h-4 w-4 text-amber-500" />
            <div className="h-px w-20 bg-gradient-to-l from-transparent to-amber-600" />
          </div>

          {/* count badge */}
          <div className="flex items-center justify-center gap-3">
            <Badge
              variant="outline"
              className="border-amber-700/50 bg-amber-950/50 text-amber-400 font-sans px-5 py-1.5 text-xs tracking-widest"
            >
              <Images className="h-3.5 w-3.5 mr-2" />
              {IMAGES.length} {t.gallery.moments}
            </Badge>
          </div>
        </div>

        {/* bottom fade */}
        <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-stone-950 to-transparent" />
      </section>

      {/* ── STICKY FILTER BAR ─────────────────────────────────── */}
      <section className="sticky top-0 z-30 border-b border-stone-800/80 bg-stone-950/90 backdrop-blur-xl">
        {/* top accent line */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-700/40 to-transparent" />
        <div className="mx-auto max-w-6xl px-4 py-4">
          <Tabs
            value={activeCategory}
            onValueChange={(v: string) => setActiveCategory(v as Category)}
          >
            <TabsList className="flex h-auto flex-wrap gap-2 bg-transparent p-0 justify-center w-full">
              {CATEGORIES.map((cat) => (
                <TabsTrigger
                  key={cat.value}
                  value={cat.value}
                  className="rounded-full border border-stone-700/70 px-5 py-2.5 font-sans text-sm font-medium text-stone-400
                    transition-all duration-200
                    hover:border-amber-600/50 hover:bg-amber-950/30 hover:text-amber-300
                    data-[state=active]:border-amber-600 data-[state=active]:bg-gradient-to-br
                    data-[state=active]:from-amber-950/60 data-[state=active]:to-orange-950/40
                    data-[state=active]:text-amber-300 data-[state=active]:shadow-[0_0_20px_rgba(217,119,6,0.25)]"
                >
                  {cat.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </section>

      {/* ── GRID ──────────────────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
        {filtered.length === 0 ? (
          <div className="flex min-h-64 items-center justify-center text-stone-500 font-sans">
            {t.gallery.noImages}
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
            {filtered.map((img, idx) => {
              const alt = language === "ta" ? img.altTa : img.altEn;
              const label = language === "ta" ? img.labelTa : img.labelEn;

              return (
                <div
                  key={img.id}
                  onClick={() => openLightbox(idx)}
                  className={`group relative block w-full cursor-pointer overflow-hidden rounded-2xl
                    border border-stone-800 bg-stone-900 break-inside-avoid
                    shadow-lg shadow-stone-950/60
                    transition-all duration-300
                    hover:-translate-y-1
                    hover:border-amber-700/50
                    hover:shadow-[0_12px_40px_rgba(180,83,9,0.28)]
                    ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                >
                  {/* image */}
                  <div className="relative w-full overflow-hidden">
                    <Image
                      src={img.src}
                      alt={alt}
                      width={600}
                      height={400}
                      className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.06]"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    />

                    {/* overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-stone-950/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    {/* zoom icon */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-11 h-11 rounded-full bg-amber-500/90 flex items-center justify-center scale-0 group-hover:scale-100 transition-transform duration-300 shadow-lg shadow-amber-900/50">
                        <ZoomIn className="h-5 w-5 text-stone-950" />
                      </div>
                    </div>

                    {/* category badge -  bottom left on hover */}
                    <div className="absolute bottom-3 left-3 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                      <span className={`font-sans text-[10px] font-semibold tracking-widest uppercase px-2.5 py-1 rounded-full border ${catBadgeClass[img.category] ?? catBadgeClass.deity}`}>
                        {label}
                      </span>
                    </div>

                    {/* index number -  top right */}
                    <div className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-stone-950/70 border border-stone-700/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="font-sans text-[10px] font-bold text-stone-300">{idx + 1}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* results count */}
        {filtered.length > 0 && (
          <div className="mt-12 flex items-center justify-center gap-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-800/40" />
            <p className="font-sans text-xs text-stone-500 tracking-widest uppercase">
              {filtered.length} {language === "ta" ? "படங்கள்" : "Photos"}
            </p>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-800/40" />
          </div>
        )}
      </section>

      {/* ── LIGHTBOX ──────────────────────────────────────────── */}
      <Dialog
        open={lightboxIndex !== null}
        onOpenChange={(open: boolean) => !open && closeLightbox()}
      >
        <DialogContent
          className="max-w-[96vw] xl:max-w-screen-xl border-stone-800 bg-stone-950/98 p-0 shadow-2xl backdrop-blur-2xl [&>button]:hidden outline-none rounded-2xl overflow-hidden"
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">
            {(language === "ta" ? currentImage?.altTa : currentImage?.altEn) ?? "Gallery image"}
          </DialogTitle>

          {currentImage && (
            <div className="flex flex-col">

              {/* lightbox header */}
              <div className="flex items-center justify-between border-b border-stone-800/80 px-5 py-3.5 bg-stone-900/60">
                <div className="flex items-center gap-3">
                  <span className={`font-sans text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full border ${catBadgeClass[currentImage.category] ?? catBadgeClass.deity}`}>
                    {language === "ta" ? currentImage.labelTa : currentImage.labelEn}
                  </span>
                  <span className="font-sans text-xs text-stone-500">
                    {(lightboxIndex ?? 0) + 1}
                    <span className="mx-1 text-stone-700">/</span>
                    {filtered.length}
                  </span>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={closeLightbox}
                  className="h-9 w-9 rounded-xl text-stone-400 hover:text-stone-100 hover:bg-stone-800"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* image area */}
              <div className="relative h-[65vh] sm:h-[72vh] w-full overflow-hidden bg-stone-900/40">
                <Image
                  src={currentImage.src}
                  alt={language === "ta" ? currentImage.altTa : currentImage.altEn}
                  fill
                  sizes="(max-width: 1280px) 100vw, 1280px"
                  className="object-contain"
                  priority
                />

                {/* side nav buttons overlaid on image */}
                <button
                  onClick={prev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-stone-950/70 border border-stone-700/60 flex items-center justify-center text-stone-300 hover:border-amber-600/60 hover:text-amber-300 hover:bg-amber-950/60 transition-all duration-200 backdrop-blur-sm"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={next}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-stone-950/70 border border-stone-700/60 flex items-center justify-center text-stone-300 hover:border-amber-600/60 hover:text-amber-300 hover:bg-amber-950/60 transition-all duration-200 backdrop-blur-sm"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>

              {/* lightbox footer */}
              <div className="flex items-center justify-between border-t border-stone-800/80 px-5 py-3.5 bg-stone-900/60 gap-4">
                <p className="font-sans text-sm text-stone-400 leading-snug line-clamp-1 flex-1">
                  {language === "ta" ? currentImage.altTa : currentImage.altEn}
                </p>

                {/* progress dots */}
                <div className="hidden sm:flex items-center gap-1.5 shrink-0">
                  {filtered.slice(
                    Math.max(0, (lightboxIndex ?? 0) - 2),
                    Math.min(filtered.length, (lightboxIndex ?? 0) + 3)
                  ).map((_, dotOffset) => {
                    const realIdx = Math.max(0, (lightboxIndex ?? 0) - 2) + dotOffset;
                    const isActive = realIdx === lightboxIndex;
                    return (
                      <button
                        key={realIdx}
                        onClick={() => setLightboxIndex(realIdx)}
                        className={`rounded-full transition-all duration-200 ${
                          isActive
                            ? "w-5 h-2 bg-amber-500"
                            : "w-2 h-2 bg-stone-600 hover:bg-stone-400"
                        }`}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── FOOTER ORNAMENT ───────────────────────────────────── */}
      <div className="h-px bg-gradient-to-r from-transparent via-amber-700/40 to-transparent" />
      <footer className="py-10 text-center space-y-3">
        <div className="flex items-center justify-center gap-3">
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-800/40" />
          <Sparkles className="h-4 w-4 text-amber-700/60" />
          <Flame className="h-5 w-5 text-amber-500" />
          <Sparkles className="h-4 w-4 text-amber-700/60" />
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-800/40" />
        </div>
        <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-stone-600">
          {language === "ta"
            ? "ஸ்ரீ கருப்பண்ணசாமி திருக்கோயில்"
            : "SRI KARUPPANNASWAMY TEMPLE"}
          &nbsp;·&nbsp;
          {t.gallery.imageTitle}
        </p>
      </footer>
    </main>
  );
}